<template>
    <div>
        <v-card class="beginning-inventories" outlined>
            <v-card-title class="page-heading primary">
                <v-row align="center">
                    <v-col cols="12" sm="9">{{ $t('beginning_inventories.heading') }}</v-col>
                    <v-col cols="12" sm="3" class="d-flex justify-end">
                        <v-menu
                            transition="slide-y-transition"
                            bottom
                            right
                        >
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                    class="minw100"
                                    color="white"
                                    outlined
                                    v-bind="attrs"
                                    v-on="on"
                                >
                                    <v-icon left>fal fa-plus</v-icon>
                                    {{ $t('general.add') }}
                                </v-btn>
                            </template>

                            <v-list>
                                <v-list-item
                                    key="1"
                                    @click="openForm"
                                >
                                    <v-list-item-title>{{ $t('beginning_inventories.create_new') }}</v-list-item-title>
                                </v-list-item>
                                <v-list-item
                                    key="2"
                                    :disabled="busy"
                                    @click="autoCreate"
                                >
                                    <v-list-item-title>{{ $t('beginning_inventories.create_from_stock') }}</v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </v-col>
                </v-row>
            </v-card-title>

            <v-card-text>
                <beginning-inventories-search
                    :filter.sync="filter"
                />

                <v-data-table
                    class="items-table operations striped"
                    :headers="headers"
                    :items="items"
                    :options.sync="pagination"
                    :footer-props="{ 'items-per-page-options': [20, 50, 100, 200] }"
                    :items-per-page.sync="pagination.itemsPerPage"
                    :server-items-length="itemsTotal"
                    :loading="loading"
                    :mobile-breakpoint="0"
                    dense
                >
                    <template v-slot:foot="{ items }">
                        <tfoot>
                            <tr>
                                <td></td>
                                <td></td>
                                <td></td>
                                <td>{{ $t('general.total').toUpperCase() }}:</td>
                                <td>{{ items.reduce((acc, cur) => acc.add(cur.getSellingValue()), Money.parse(0)).toFormat('0.00') }}</td>
                                <td>{{ items.reduce((acc, cur) => acc.add(cur.getTax()), Money.parse(0)).toFormat('0.00') }}</td>
                                <td>{{ items.reduce((acc, cur) => acc.add(cur.getSellingValueAfterTax()), Money.parse(0)).toFormat('0.00') }}</td>
                                <td></td>
                            </tr>
                        </tfoot>
                    </template>
                    <template v-slot:item="{ item }">
                        <tr>
                            <td>{{ item.date | date }}</td>
                            <td>
                                <v-chip
                                    style="z-index: 0"
                                    small
                                    outlined
                                    :color="item.recorded_at ? 'success' : 'error'"
                                >
                                    {{ item.recorded_at ? $t('general.yes') : $t('general.no') }}
                                </v-chip>
                            </td>
                            <td>{{ item.number }}</td>
                            <td>{{ item.description }}</td>
                            <td class="primary--text font-weight-bold">{{ item.getSellingValue().toFormat('0.00') }}</td>
                            <td>{{ item.getTax().toFormat('0.00') }}</td>
                            <td class="primary--text font-weight-bold">{{ item.getSellingValueAfterTax().toFormat('0.00') }}</td>
                            <td align="center" class="w35 maxw60">
                                <v-menu bottom transition="slide-y-transition">
                                    <template v-slot:activator="{ on }">
                                        <v-btn v-on="on" icon>
                                            <v-icon>far fa-ellipsis-h</v-icon>
                                        </v-btn>
                                    </template>

                                    <v-list>
                                        <v-list-item
                                            key="print"
                                            :disabled="!item.recorded_at"
                                            @click="print(item)"
                                        >
                                            <v-list-item-action class="mr-4 flex justify-content-center">
                                                <v-icon :disabled="!item.recorded_at">fal fa-print</v-icon>
                                            </v-list-item-action>
                                            <v-list-item-title>{{ $t('general.print') }}</v-list-item-title>
                                        </v-list-item>

                                        <v-list-item
                                            key="view"
                                            @click="openForm(item)"
                                        >
                                            <v-list-item-action class="mr-4 flex justify-content-center">
                                                <v-icon>fal fa-eye</v-icon>
                                            </v-list-item-action>
                                            <v-list-item-title>{{ $t('general.view') }}</v-list-item-title>
                                        </v-list-item>

                                        <v-divider />

                                        <v-list-item
                                            key="delete"
                                            :disabled="!!item.recorded_at"
                                            @click="deleteItem(item)"
                                        >
                                            <v-list-item-action class="mr-4 flex justify-content-center">
                                                <v-icon
                                                    :disabled="!!item.recorded_at"
                                                    :class="{'error--text': !item.recorded_at}"
                                                >
                                                    fal fa-times
                                                </v-icon>
                                            </v-list-item-action>
                                            <v-list-item-title :class="{'error--text': !item.recorded_at}">
                                                {{ $t('general.delete') }}
                                            </v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>
                            </td>
                        </tr>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>
    </div>
</template>

<script>
import ApiService from '@/services/ApiService';
import BeginningInventory from '@/models/BeginningInventory';
import BeginningInventoriesSearch from '@/views/shared/admin/beginningInventories/BeginningInventoriesSearch';
import HtmlGen from '@/wrappers/HtmlGen';

export default {
    components: {
        BeginningInventoriesSearch,
    },

    data() {
        return {
            items: [],
            itemsTotal: 0,
            loading: false,

            filter: {
                search: null,
                startDate: moment().subtract(1, 'month').format('YYYY-MM-DD') + ' 00:00:00',
                endDate: moment().format('YYYY-MM-DD') + ' 23:59:59',
            },

            headers: [
                { text: this.$t('general.date'), value: 'date' },
                { text: this.$t('general.recorded'), value: 'recorded_at' },
                { text: this.$t('general.number'), value: 'number' },
                { text: this.$t('beginning_inventories.description'), value: 'description', sortable: false },
                { text: this.$t('beginning_inventories.selling_value'), value: 'selling_value', sortable: false },
                { text: this.$t('beginning_inventories.tax'), value: 'tax', sortable: false },
                { text: this.$t('beginning_inventories.selling_value_after_tax'), value: 'selling_value_after_tax', sortable: false },
                { text: '', sortable: false }
            ],

            pagination: {
                sortBy: ['date'],
                itemsPerPage: 100,
                page: 1,
                sortDesc: [true],
            },

            busy: false
        };
    },

    watch: {
        pagination: {
            handler() {
                this.getItems();
            },
            deep: true
        },

        filter() {
            this.getItems();
        },
    },

    methods: {
        getItems() {
            const { sortBy, sortDesc, page, itemsPerPage } = this.pagination;
            const sortField = sortBy[0] ? sortBy[0] : 'date';
            const sortOrder = sortDesc[0] ? 'descending' : 'ascending';

            let url = `/admin/beginning-inventories?page=${page}&itemsPerPage=${itemsPerPage}&sortField=${sortField}&sortOrder=${sortOrder}`;

            if (this.filter) {
                Object.keys(this.filter).forEach(key => {
                    if(this.filter[key]) {
                        url += `&${key}=${this.filter[key]}`;
                    }
                })
            }

            this.loading = true;

            ApiService.get(url).then(response => {
                this.loading = false;
                this.items = response.data.data.map(item => new BeginningInventory(item));
                this.itemsTotal = response.data.total;
            });
        },

        openForm(item = null) {
            if(!item) {
                this.$router.push({ name: 'adminBeginningInventoryCreate' }).catch(err => {});
                return;
            }

            this.$router.push({ name: 'adminBeginningInventoryUpdate', params: { id: item.id } }).catch(err => {});
        },

        deleteItem(item) {
            this.$confirm(
                this.$t('beginning_inventories.permanently_delete'),
                {
                    buttonTrueText: this.$t('general.confirm'),
                    icon: 'fal fa-times',
                    color: 'error',
                    title: this.$t('general.delete')
                },
                result => {
                    if (!result) return;

                    ApiService.delete(`/admin/beginning-inventories/${item.id}`, {}).then(response => {
                        this.items = this.items.filter(i => i.id !== item.id);
                        this.$snackbar(this.$t('beginning_inventories.deleted'), 'success');
                        this.itemsTotal--;
                    });
                }
            );
        },

        print(item) {
            const html = item.toTextLines().map(line => HtmlGen.fromTextLine(line)).join('');
            this.$printService.printCustom(null, html, null, { choosePrinter: true })
        },

        autoCreate() {
            this.busy = true

            ApiService.post('/admin/beginning-inventories/create-from-stock', {}).then(response => {
                this.busy = false

                if(!response.data.success) {
                    this.$snackbar(this.$t(`beginning_inventories.error.${response.data.error}`), 'error');
                    return
                }

                this.items.push(new BeginningInventory(response.data.beginningInventory))
                this.itemsTotal++;
                this.$snackbar(this.$t('beginning_inventories.saved'), 'success')
            });
        }
    }
};
</script>

<style lang="scss" scoped>
.beginning-inventories {
    .page-heading {
        padding: 12px 16px;
        color: white;
        font-size: 16px;
        word-break: break-word;
    }

    ::v-deep .items-table {
        margin-top: 28px;

        th {
            font-size: 11px;
            padding: 0 4px;
            max-width: 80px;

            span {
                white-space: break-spaces;
            }
        }

        td {
            font-size: 13px;
            padding: 0 4px;
        }

        .separator {
            border-left: 1px solid rgba(0, 0, 0, 0.5);
            padding-left: 12px;
        }

        tfoot {
            font-weight: 500;
            background: #f3f4fb;
        }
    }
}
</style>
